import { KeyMetricDataPoint } from 'src/types';

export type TrendResult = {
  trend: 'up' | 'down';
  trendValue: string;
};

/**
 * Calculates trend direction and percentage change of a value against a
 * baseline (e.g. optimized vs initial, scenario vs comparison scenario)
 */
export const calculateTrendFromComparison = (
  value: number,
  baseline: number,
): TrendResult => {
  const delta = value - baseline;
  const direction: 'up' | 'down' = delta >= 0 ? 'up' : 'down';
  const pct =
    baseline === 0 ? 0 : Math.round((delta / Math.abs(baseline)) * 100);

  return { trend: direction, trendValue: `${Math.abs(pct)}%` };
};

/**
 * Calculates trend direction and percentage change from time series data
 * @param points Array of time series points with value and utcEpoch
 * @returns Object with trend direction and trendValue percentage
 */
export const calculateTrendFromTimeSeries = (
  points: KeyMetricDataPoint[],
): TrendResult => {
  // Sort by utcEpoch to get chronological order
  const sortedPoints = points
    .filter((p) => typeof p?.value === 'number' && !Number.isNaN(p.value))
    .sort((a, b) => a.utcEpoch - b.utcEpoch);

  if (sortedPoints.length < 2) {
    return { trend: 'up', trendValue: '0%' };
  }

  const first = sortedPoints[0].value ?? 0;
  const last = sortedPoints[sortedPoints.length - 1].value ?? 0;
  const delta = last - first;
  const direction: 'up' | 'down' = delta >= 0 ? 'up' : 'down';
  const pct = first === 0 ? 0 : Math.round((delta / Math.abs(first)) * 100);

  return { trend: direction, trendValue: `${Math.abs(pct)}%` };
};
